import { isBefore, startOfDay } from 'date-fns';
import { Attempt, LessonsPlanLesson } from 'state/modules/entity/types';
import { cloneDeep, find, isNil } from 'lodash';
import { DragIndices } from 'state/hooks/ui/use-dragdrop';
import { formatFullDate, getWeekday, isEqualDate } from 'util/date-util';
import { NON_EDIT_ATTEMPT_STATUSES } from 'types';

// CSC - FIX
export const getWeekdays = (locale: string): string[] =>
  locale === 'en-US'
    ? ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday']
    : ['Lunes', 'Martes', 'Miércoles', 'Jueves', 'Viernes'];

export const getWeekStartingIndex = (week: number): number => week * 5 - 5;

export const fillDayGaps = (lessons: Array<LessonsPlanLesson> | any, locale: string): any => {
  if (!lessons) return;
  const weekdays = getWeekdays(locale);
  let dayIdx = weekdays.indexOf(lessons[0].dayOfWeek);

  //fill front
  if (dayIdx !== 0) {
    const fillDays = weekdays.slice(0, dayIdx);
    return fillDays
      .map(fillDay => ({
        lessonDate: null,
        dayOfWeek: fillDay
      }))
      .concat(lessons as any);
  }
  //fill end
  if (lessons.length % 5 > 0) {
    dayIdx = weekdays.indexOf(lessons[lessons.length - 1].dayOfWeek);
    const fillDays = weekdays.slice(dayIdx + 1, lessons.length - 1);
    return lessons.concat(
      fillDays.map(fillDay => ({
        lessonDate: null,
        dayOfWeek: fillDay
      }))
    );
  }

  return lessons;
};

export const getLessonPlanWeekIndex = (lessons: any[]): number => {
  const weekday = formatFullDate(getWeekday());
  return lessons.findIndex(({ lessonDate, date }) => isEqualDate(lessonDate || date, weekday));
};

export const swapLessonItems = (lessons, { originIndices, targetIndices, targetDate }) => {
  const lessonsClone: Array<any> = cloneDeep(lessons);
  const targetDay = find(lessonsClone, ({ lessonDate }) => isEqualDate(lessonDate, targetDate));
  const targetCol =
    targetIndices?.col ||
    lessonsClone.findIndex(({ lessonDate }) => isEqualDate(lessonDate, targetDate));

  const originLessonCard = lessonsClone[originIndices.col].lessons[originIndices.item];
  const targetCard = { lessons: [], ...targetDay };
  const targetLessons = targetCard.lessons[targetIndices?.item || 0];

  const originLessons = lessonsClone[originIndices.col].lessons.length;

  // if the target doesnt have lessons
  if (!targetLessons) {
    if (originLessons > 1) {
      const oCard = lessonsClone[originIndices.col].lessons.splice(originIndices?.item || 0, 1);

      lessonsClone[targetCol] = {
        ...lessonsClone[targetCol],
        lessons: oCard
      };
    } else {
      const oCard = lessonsClone[originIndices.col].lessons.splice(0, 1);

      lessonsClone[targetCol] = {
        ...lessonsClone[targetCol],
        lessons: oCard
      };
    }
  } else {
    lessonsClone[originIndices.col].lessons[originIndices.item] =
      targetCard.lessons[targetIndices?.item || 0];

    lessonsClone[targetCol].lessons[targetIndices?.item || 0] = originLessonCard;
  }

  return lessonsClone;
};

export const combineLessonItems = (lessons, { originIndices, targetIndices, targetDate }) => {
  const lessonsClone: any[] = cloneDeep(lessons);
  const targetDay = find(lessonsClone, ({ lessonDate }) => isEqualDate(lessonDate, targetDate));
  const [moveItem] = lessonsClone[originIndices.col].lessons.splice(originIndices.item, 1);
  const targetIndex = lessonsClone.findIndex(({ lessonDate }) =>
    isEqualDate(lessonDate, targetDay.lessonDate)
  );
  const combinedItem = {
    combined: true,
    ...moveItem
  };

  targetDay.lessons.push(combinedItem);
  const remainingTargetArr = targetDay.lessons
    .filter(value => Object.keys(value).length !== 0)
    .map((x, idx) => (idx !== 0 ? { combined: true, ...x } : x));

  lessonsClone[targetIndices?.col || targetIndex].lessons = remainingTargetArr;

  return lessonsClone.filter(value => Object.keys(value).length !== 0);
};

export const composeInstructionDate = (date: string | Date, skip = true) => ({
  date: formatFullDate(startOfDay(new Date(date))),
  skip
});

export const composeEmptyDate = (date: string | Date, empty = true) => ({
  date: formatFullDate(startOfDay(new Date(date))),
  empty
});

export const filterBumpLessonDates = (lessons, { originIndices, targetDate }) => {
  const originDay = lessons[originIndices.col];
  const originIndex = lessons.findIndex(({ lessonDate }) =>
    isEqualDate(lessonDate, originDay.lessonDate)
  );
  const targetIndex = lessons.findIndex(({ lessonDate }) => isEqualDate(lessonDate, targetDate));

  const beginIndex = Math.min(originIndex, targetIndex);
  const endIndex = Math.max(originIndex, targetIndex);
  const empty = originIndex < targetIndex;

  const lessonsBumped = lessons.slice(beginIndex, endIndex).filter(({ lessons }) => lessons).length;
  const emptySlots = lessons.slice(endIndex).filter(({ lessons }) => !lessons);

  return [
    ...lessons
      .slice(beginIndex, endIndex)
      .map(({ lessonDate }) => composeEmptyDate(lessonDate, empty)),
    ...emptySlots
      .slice(0, lessonsBumped)
      .map(({ lessonDate }) => composeEmptyDate(lessonDate, !empty))
  ];
};

const getCombined = (combined, lessons: any = [], idx) =>
  (combined && { combined: true }) || (lessons.length > 1 && idx !== 0 && { combined: true });

export const groupTeachingOrderLessonIds = (lessonsGroups: LessonsPlanLesson[]): any =>
  lessonsGroups.reduce((acc: any, { lessons = [] }: LessonsPlanLesson) => {
    if (!lessons.length) return acc;
    acc.push(
      ...lessons.map(({ teachingOrderLessonId, combined }: any, idx) => ({
        teachingOrderLessonId,
        ...getCombined(combined, lessons, idx)
      }))
    );
    return acc;
  }, []);

export const generateTeachingOrderName = (teachingOrder: any[], name: string) => {
  if (!teachingOrder.find(i => i.name === name)) return name;

  const [baseName, count = 0] = name.split(' - ');
  const nextName = `${baseName} - ${Number.parseInt(count as any, 10) + 1}`;
  return generateTeachingOrderName(teachingOrder, nextName);
};

export const calcWorkbookPreviewSize = (windowHeight: number, isPortrait: boolean) => {
  if (windowHeight > 1080 && isPortrait) {
    return { width: 800, height: 1050 };
  } else if (windowHeight > 1030 && !isPortrait) {
    return { width: 1100, height: 880 };
  }

  let width;
  const height = window.innerHeight - 50;
  if (isPortrait) {
    width = height * 0.76;
  } else {
    width = height * 1.2;
  }
  return { width: width, height: height };
};

export const isReleasedToStudents = ({ dateAssigned }) =>
  isBefore(new Date(dateAssigned), new Date());

export const hasStartedOrCompletedAttempts = (attempts: Array<Attempt> = []) => {
  return attempts.some(
    ({ status }) =>
      status === NON_EDIT_ATTEMPT_STATUSES.IN_PROGRESS ||
      status === NON_EDIT_ATTEMPT_STATUSES.COMPLETED
  );
};

export const getStartedAndCompletedAttempts = (attempts: Array<Attempt> = []) =>
  attempts.reduce(
    (acc, { status }) => {
      if (status === NON_EDIT_ATTEMPT_STATUSES.IN_PROGRESS) acc.started += 1;
      if (status === NON_EDIT_ATTEMPT_STATUSES.COMPLETED) acc.completed += 1;
      return acc;
    },
    { completed: 0, started: 0 }
  );

export const moveLessonToEnd = (lessons, { col, item: lessonIdx }: DragIndices) => {
  const lessonsClone: any[] = cloneDeep(lessons);
  const card = lessonsClone[col].lessons[lessonIdx];

  lessonsClone[col].lessons.splice(lessonIdx, 1);
  const lastLesson = lessonsClone[lessonsClone.length - 1];
  if (lastLesson?.lesson) lastLesson.lessons.push(card);
  else lastLesson['lessons'] = [card];
  return lessonsClone;
};

export const moveLessons = (lessons, { originIndices, targetDate }) => {
  const lessonsClone: any[] = cloneDeep(lessons);
  const originDay = lessonsClone[originIndices.col];
  const targetDay = find(lessonsClone, ({ lessonDate }) => isEqualDate(lessonDate, targetDate));

  const moveItem = originDay.lessons.splice(originIndices.item, 1);
  const targetIndex = lessonsClone.findIndex(({ lessonDate }) =>
    isEqualDate(lessonDate, targetDay.lessonDate)
  );

  return lessonsClone.reduce((acc, c, idx) => {
    if (idx === targetIndex) c.lessons = moveItem;
    acc.push(c);
    return acc;
  }, []);
};

export const createEmptyDatesByOriginAndTarget = (lessons, { originIndices, targetDate }) => {
  const emptyDates: any[] = [];
  const originDay = lessons[originIndices.col];
  const targetDay = lessons.find(({ lessonDate }) => isEqualDate(lessonDate, targetDate));

  if (originDay.lessons.length === 1) emptyDates.push(composeEmptyDate(originDay.lessonDate));

  if (!targetDay.lessons) emptyDates.push(composeEmptyDate(targetDay.lessonDate, false));

  return emptyDates;
};

export const createEmptyDatesAfterSwap = (lessons, { originIndices, targetDate }) => {
  const emptyDates: any[] = [];
  const originDay = lessons[originIndices.col];
  const targetDay = lessons.find(({ lessonDate }) => isEqualDate(lessonDate, targetDate));

  if (!originDay.lessons.length) emptyDates.push(composeEmptyDate(originDay.lessonDate));

  if (targetDay.lessons) emptyDates.push(composeEmptyDate(targetDay.lessonDate, false));

  return emptyDates;
};

/** this is needed to calculate the relative col when a move is triggered by the 3 dot menu */
export const getColRelativeToWeek = (dragCol, currentLessonWeek): number =>
  dragCol + getWeekStartingIndex(currentLessonWeek);

export const getUserHasTeachingOrder = (user, selectedProductCurriculumId): boolean => {
  const userHasTeachingOrder =
    user?.settings?.teachingOrder &&
    user?.settings?.teachingOrder.find(
      ({ productCurriculumId, startDate }) =>
        selectedProductCurriculumId === productCurriculumId && !isNil(startDate)
    );
  return userHasTeachingOrder;
};
